// src/components/ProtectedRoute.tsx
import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAccount } from "../contexts/AccountContext";
import LoadingView from "./LoadingView";
import { Heading } from "../design/heading";
import { Text } from "../design/text";

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectPath = "/login",
}) => {
  const { user, loading: loadingUser, isMonitor } = useAuth();
  const { account, loading: loadingAccount, error } = useAccount();

  if (loadingUser) {
    return <LoadingView />;
  }

  if (!user) {
    return <Navigate to={redirectPath} replace />;
  }

  // Monitors should not access admin-only routes - redirect to dashboard
  // Note: This ProtectedRoute is only used for admin-specific routes like onboarding
  if (isMonitor) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loadingAccount) {
    return <LoadingView />;
  }

  // If no account found, show error instead of infinite reload loop
  if (!account) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8">
        <Heading>Account Not Found</Heading>
        <Text className="text-center text-zinc-600 dark:text-zinc-400">
          {error || "Unable to load your account. Please try signing out and back in."}
        </Text>
        <a href="/login" className="text-blue-600 hover:underline">
          Return to login
        </a>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
